import { useCallback, useEffect, useState } from "react"
import { useQuery } from "urql"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type {
  Company,
  CompanyListDetailsQuery,
  CompanyListDetailsQueryVariables,
  CompanyListSuccess,
} from "@/graphql/codegen/graphql"
import { CompanyListDetailsDocument } from "@/graphql/codegen/graphql"
import useDebounce from "@/utils/useDebounce"

const LIMIT = 20

interface UseGetShipViaQueryReturn {
  data: Company[]
  fetching: boolean
  loadMore: () => void
  triggerQuery: () => void
  setSearchTerm: (searchTerm: string) => void
}

export const useGetShipViaQuery = (): UseGetShipViaQueryReturn => {
  const [offset, setOffset] = useState(0)
  const [intermediaryLocations, setIntermediaryLocations] = useState<Company[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY_MS)

  const [{ data, fetching }, queryCompanies] = useQuery<CompanyListDetailsQuery, CompanyListDetailsQueryVariables>({
    query: CompanyListDetailsDocument,
    variables: {
      input: {
        filter: { types: ["TRANSPORTATION"], query: debouncedSearchTerm },
        limit: LIMIT,
        offset,
      },
    },
  })

  useEffect(() => {
    setOffset(0)
    setIntermediaryLocations([])
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (data) {
      const companyList = (data?.company.list as CompanyListSuccess)?.companies || []

      setIntermediaryLocations((prev) => (offset === 0 ? companyList : [...prev, ...companyList]))
    }
  }, [data, offset])

  const loadMore = useCallback(() => {
    setOffset((prevOffset) => prevOffset + LIMIT)
  }, [])

  const triggerQuery = useCallback(() => queryCompanies(), [queryCompanies])

  return {
    data: intermediaryLocations,
    fetching,
    loadMore,
    triggerQuery,
    setSearchTerm,
  }
}
